import { Link } from "react-router-dom";
import { moneyAud, formatDateTime } from "../../utils/portalHelpers";
import {
  PageHeader,
  Panel,
  StatCard,
  StatusChip,
  EmptyState,
  LoadingPanel,
  ErrorPanel,
  chipTone,
} from "../../components/ui";
import { DOCUMENT_REQUEST_STATUSES, labelFor, toneFor } from "const/referralEnums";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerDashboard } from "../../api/endpoints/workspace";
import { fetchPartnerOffers } from "../../api/endpoints/program";
import { STAT_LINKS, activityLink, earnedTotal } from "./dashboardHelpers";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import OffersPanel from "./OffersPanel";
import OfferPopup from "./OfferPopup";
import IncompleteDetailsBanner from "./IncompleteDetailsBanner";
import RecentApplicationsPanel from "./RecentApplicationsPanel";

const QuickLink = ({ to, label, hint }) => (
  <Link
    to={to}
    className="rounded-xl border border-slate-200 bg-white px-4 py-3 no-underline hover:border-teal-300"
  >
    <p className="text-sm font-semibold text-slate-900 m-0">{label}</p>
    <p className="text-xs text-slate-500 m-0 mt-0.5">{hint}</p>
  </Link>
);

const ActivityRow = ({ item }) => {
  const to = activityLink(item);
  const title = item.title || "Untitled";
  return (
    <li className="flex items-start justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="min-w-0">
        {to ? (
          <Link
            to={to}
            className="block text-sm font-medium text-slate-900 no-underline truncate hover:text-teal-700"
          >
            {title}
          </Link>
        ) : (
          <p className="text-sm font-medium text-slate-900 m-0 truncate">{title}</p>
        )}
        <p className="text-xs text-slate-500 m-0 mt-0.5">{formatDateTime(item.at)}</p>
      </div>
      {item.subtitle ? (
        <StatusChip tone={chipTone(toneFor(DOCUMENT_REQUEST_STATUSES, item.subtitle))}>
          {labelFor(DOCUMENT_REQUEST_STATUSES, item.subtitle)}
        </StatusChip>
      ) : null}
    </li>
  );
};

const DashboardPage = () => {
  const {
    data: dashboard,
    isLoading,
    isError,
    refetch,
  } = usePartnerQuery(partnerQueryKeys.dashboard(), fetchPartnerDashboard);

  // Its own query: an offer the office is running is not part of this partner's figures, and a
  // failure to load one must not cost them the dashboard. Published offers barely move within a
  // visit, so remounts within five minutes reuse the cache instead of refetching.
  const { data: offers } = usePartnerQuery(partnerQueryKeys.offers(), fetchPartnerOffers, {
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Dashboard" />
        <LoadingPanel label="Loading your workspace…" />
      </div>
    );
  }

  if (isError || !dashboard) {
    return (
      <div>
        <PageHeader title="Dashboard" />
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      </div>
    );
  }

  const activity = dashboard.recentActivity || [];

  return (
    <div>
      <OfferPopup offers={offers} />
      <IncompleteDetailsBanner />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Clients" value={dashboard.clientCount ?? 0} to={STAT_LINKS.clients} />
        <StatCard
          label="Leads waiting on you"
          value={dashboard.leadsAwaitingMe ?? 0}
          tone={dashboard.leadsAwaitingMe ? "warn" : "default"}
          to={STAT_LINKS.leads}
        />
        <StatCard
          label="Documents outstanding"
          value={dashboard.documentRequestsOutstanding ?? 0}
          tone={dashboard.documentRequestsOutstanding ? "warn" : "default"}
          to={STAT_LINKS.documents}
        />
        <StatCard
          label="Open tasks"
          value={dashboard.openTasks ?? 0}
          tone={dashboard.openTasks ? "warn" : "default"}
          to={STAT_LINKS.tasks}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-4 lg:col-span-2">
          <Panel title="Recent activity">
            {activity.length ? (
              <ul className="list-none p-0 m-0">
                {activity.map((item, index) => (
                  <ActivityRow key={`${item.kind}-${item.title}-${index}`} item={item} />
                ))}
              </ul>
            ) : (
              <EmptyState
                title="No activity yet"
                description="Referrals, document requests and payouts will show up here as they happen."
              />
            )}
          </Panel>
          <RecentApplicationsPanel />
        </div>

        <div className="grid grid-cols-1 gap-3 content-start">
          <OffersPanel offers={offers} />
          <QuickLink
            to="/partner/clients?tab=leads"
            label="Refer a client"
            hint="Start a new referral"
          />
          <QuickLink
            to="/partner/documents"
            label="Collect documents"
            hint="Requests routed to you"
          />
          <QuickLink to="/partner/payments" label="Your earnings" hint="Commission and payouts" />
          {dashboard.unreadMessages ? (
            <QuickLink
              to="/partner/alerts"
              label={`${dashboard.unreadMessages} unread message${
                dashboard.unreadMessages === 1 ? "" : "s"
              }`}
              hint="From the office"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
